import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { GameController, Plus, PencilSimple, Trash, X, Check, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DataTable, FilterBar, StatusBadge, DetailDrawer } from '../../components/admin/AdminComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COMPLEXITIES = ['light', 'medium', 'heavy', 'expert'];

const EMPTY_GAME = {
  name: '', publisher: '', description: '', categories: [],
  min_players: 2, max_players: 4, play_time: '', complexity: 'medium', image: '',
};

export default function AdminGames() {
  const [games, setGames] = useState([]);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingGames, setPendingGames] = useState([]);
  const [tab, setTab] = useState('approved'); // 'approved' | 'pending'
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [drawerGame, setDrawerGame] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  // Modal state for add/edit
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_GAME);
  const [saving, setSaving] = useState(false);
  // Pending tab: rejection modal state
  const [rejectingGame, setRejectingGame] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [decideBusy, setDecideBusy] = useState(false);
  // Bulk-approve state
  const [selectedIds, setSelectedIds] = useState([]);
  const toggleSelect = (id) => setSelectedIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const clearSelection = () => setSelectedIds([]);

  const fetchGames = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('skip', p * 50); params.append('limit', '50');
      const { data } = await axios.get(`${API_URL}/api/admin-console/games?${params}`, { withCredentials: true });
      setGames(data.games || []);
      setTotal(data.total || 0);
      setPendingCount(data.pending_count || 0);
    } catch {} finally { setLoading(false); }
  }, [search, page]);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin-console/games-pending`, { withCredentials: true });
      setPendingGames(data.pending || []);
      setPendingCount(data.total || 0);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === 'approved') fetchGames();
    else fetchPending();
  }, [tab, fetchGames, fetchPending]);

  const approvePending = async (g) => {
    setDecideBusy(true);
    try {
      await axios.post(`${API_URL}/api/admin-console/games/${g.id}/approve`, {}, { withCredentials: true });
      toast.success(`Approved "${g.name}" — submitter notified.`);
      await fetchPending();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to approve');
    } finally { setDecideBusy(false); }
  };

  const confirmReject = async () => {
    if (!rejectingGame) return;
    setDecideBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/admin-console/games/${rejectingGame.id}/reject`,
        { reason: rejectReason.trim() || undefined },
        { withCredentials: true }
      );
      toast.success(`Rejected "${rejectingGame.name}" — submitter notified.`);
      setRejectingGame(null); setRejectReason('');
      await fetchPending();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reject');
    } finally { setDecideBusy(false); }
  };

  const bulkApproveSelected = async () => {
    if (!selectedIds.length) return;
    setDecideBusy(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin-console/games/bulk-approve`,
        { game_ids: selectedIds },
        { withCredentials: true }
      );
      toast.success(`Approved ${data.approved} games — ${data.failed || 0} skipped.`);
      clearSelection();
      await fetchPending();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk approve failed');
    } finally { setDecideBusy(false); }
  };

  const bulkApproveSafeAll = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Approve every pending game whose submitter has been on CyBoard 14+ days with no moderation strikes? You can still reject the rest individually.')) return;
    setDecideBusy(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin-console/games/bulk-approve`,
        { safe_only: true },
        { withCredentials: true }
      );
      toast.success(`Approved ${data.approved} safe submissions.`);
      clearSelection();
      await fetchPending();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk approve failed');
    } finally { setDecideBusy(false); }
  };

  const openDetail = async (row) => {
    setDrawerLoading(true); setDrawerGame(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin-console/games/${row.id}`, { withCredentials: true });
      setDrawerGame(data);
    } catch {} finally { setDrawerLoading(false); }
  };

  const openAdd = () => {
    setModalMode('add'); setEditingId(null);
    setForm(EMPTY_GAME); setModalOpen(true);
  };

  const openEdit = async (row) => {
    setModalMode('edit'); setEditingId(row.id);
    // Pull full detail for the edit form
    try {
      const { data } = await axios.get(`${API_URL}/api/admin-console/games/${row.id}`, { withCredentials: true });
      setForm({
        name: data.name || '',
        publisher: data.publisher || '',
        description: data.description || '',
        categories: data.categories || [],
        min_players: data.min_players || 2,
        max_players: data.max_players || 4,
        play_time: data.play_time || '',
        complexity: data.complexity || 'medium',
        image: data.image || '',
      });
      setModalOpen(true);
    } catch { toast.error('Failed to load game'); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, categories: form.categories.filter(Boolean) };
      if (modalMode === 'add') {
        await axios.post(`${API_URL}/api/admin-console/games`, payload, { withCredentials: true });
        toast.success('Game created');
      } else {
        await axios.put(`${API_URL}/api/admin-console/games/${editingId}`, payload, { withCredentials: true });
        toast.success('Game updated');
      }
      setModalOpen(false); fetchGames();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (row.is_seed) { toast.error('Seed games cannot be deleted'); return; }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Archive game "${row.name}"? This soft-deletes it from the catalog.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin-console/games/${row.id}`, { withCredentials: true });
      toast.success('Game archived'); fetchGames();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const columns = [
    {
      key: 'name', label: 'Game',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded bg-[#9D4CDD]/10 flex items-center justify-center">
            <GameController size={14} weight="duotone" className="text-[#9D4CDD]" />
          </div>
          <div>
            <p className="text-[13px] text-white font-medium">{row.name}</p>
            <p className="text-[10px] text-[#444]">{row.publisher || '—'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'categories', label: 'Categories', cellClass: 'hidden md:table-cell',
      render: v => (
        <div className="flex flex-wrap gap-1">
          {(v || []).slice(0, 2).map(c => <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.04] text-[#666]">{c}</span>)}
        </div>
      ),
    },
    { key: 'min_players', label: 'Players', cellClass: 'hidden lg:table-cell', render: (_, row) => <span className="text-[12px] text-[#666]">{row.min_players}-{row.max_players}</span> },
    { key: 'complexity', label: 'Complexity', cellClass: 'hidden lg:table-cell', render: v => <span className="text-[12px] text-[#666] capitalize">{v || '—'}</span> },
    { key: 'session_count', label: 'Sessions', render: v => <span className="text-[12px] text-[#00F0FF] font-medium tabular-nums">{v}</span> },
    { key: 'is_seed', label: 'Source', cellClass: 'hidden xl:table-cell', render: v => <StatusBadge status={v ? 'Seed' : 'Custom'} size="xs" /> },
    {
      key: 'actions', label: '',
      render: (_, row) => (
        <div className="flex gap-1.5" onClick={e => e.stopPropagation()}>
          <button onClick={() => openEdit(row)}
            className="text-[11px] px-2 py-1 rounded-md bg-white/5 text-[#A1A1AA] hover:bg-white/10 hover:text-white inline-flex items-center gap-1"
            data-testid={`edit-game-${row.id}`}><PencilSimple size={11} weight="bold" /> Edit</button>
          {!row.is_seed && (
            <button onClick={() => remove(row)}
              className="text-[11px] px-2 py-1 rounded-md bg-[#EF4444]/10 text-[#EF4444] hover:bg-[#EF4444]/20 inline-flex items-center gap-1"
              data-testid={`delete-game-${row.id}`}><Trash size={11} weight="bold" /> Delete</button>
          )}
        </div>
      ),
    },
  ];

  const inp = "w-full bg-[#0a0a14] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm placeholder:text-[#444] focus:outline-none focus:border-[#9D4CDD]/50";

  return (
    <div data-testid="admin-games-page">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Games</h1>
          <p className="text-[13px] text-[#444] mt-0.5">{total} games in catalog</p>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#9D4CDD] text-white text-xs font-semibold hover:bg-[#7C3AED] transition-colors"
          data-testid="add-game-btn">
          <Plus size={14} weight="bold" /> Add Game
        </button>
      </div>

      <FilterBar searchValue={search} onSearchChange={setSearch} onSearch={() => { setPage(0); fetchGames(0); }}
        searchPlaceholder="Search games..." count={total} label="games" />

      {/* Approved / Pending tab bar. Pending count exposes user-submitted games
          awaiting admin review. Tapping the tab loads /games/pending and shows
          Approve / Reject actions per row (see moderation flow in
          /app/backend/admin_console.py list_pending_games / approve / reject). */}
      <div className="flex items-center gap-2 mb-4" data-testid="admin-games-tabs">
        <button
          onClick={() => setTab('approved')}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${tab === 'approved' ? 'bg-white/[0.10] text-white' : 'bg-transparent text-[#666] hover:text-white'}`}
          data-testid="admin-games-tab-approved"
        >Catalog ({total})</button>
        <button
          onClick={() => setTab('pending')}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors inline-flex items-center gap-1.5 ${tab === 'pending' ? 'bg-[#F59E0B]/15 text-[#F59E0B]' : (pendingCount > 0 ? 'bg-[#F59E0B]/10 text-[#F59E0B] hover:bg-[#F59E0B]/20' : 'bg-transparent text-[#666] hover:text-white')}`}
          data-testid="admin-games-tab-pending"
        >
          <Warning size={12} weight="fill" /> Pending Review
          {pendingCount > 0 && <span className="ml-1 tabular-nums bg-[#F59E0B]/25 text-[#F59E0B] px-1.5 py-0.5 rounded text-[10px] font-bold">{pendingCount}</span>}
        </button>
      </div>

      {tab === 'approved' ? (
        <DataTable columns={columns} data={games} loading={loading} emptyMessage="No games found" emptyIcon={GameController}
          onRowClick={openDetail} rowTestIdPrefix="game"
          page={page} pageSize={50} total={total} onPageChange={setPage}
        />
      ) : (
        <div className="space-y-2" data-testid="pending-games-list">
          {loading ? (
            <p className="text-center text-[#666] text-sm py-8">Loading…</p>
          ) : pendingGames.length === 0 ? (
            <div className="text-center py-10 border border-dashed border-white/[0.08] rounded-xl">
              <Check size={24} weight="duotone" className="mx-auto text-[#10B981] mb-2" />
              <p className="text-white text-sm font-semibold">No games awaiting review</p>
              <p className="text-[#666] text-xs mt-1">Player-submitted games will appear here.</p>
            </div>
          ) : (
            <>
              {/* Bulk-approve toolbar */}
              <div className="flex flex-wrap items-center gap-2 p-2 mb-2 rounded-lg bg-white/[0.03] border border-white/[0.06]" data-testid="bulk-approve-toolbar">
                <button
                  onClick={() => selectedIds.length === pendingGames.length ? clearSelection() : setSelectedIds(pendingGames.map(g => g.id))}
                  className="text-[11px] px-2.5 py-1.5 rounded-md bg-white/5 text-[#A1A1AA] hover:bg-white/10 font-medium"
                  data-testid="select-all-pending"
                >
                  {selectedIds.length === pendingGames.length && pendingGames.length > 0 ? 'Unselect all' : 'Select all'}
                </button>
                <button
                  onClick={bulkApproveSelected}
                  disabled={selectedIds.length === 0 || decideBusy}
                  className="text-[11px] px-2.5 py-1.5 rounded-md bg-[#10B981] text-black font-semibold hover:bg-[#0EA971] disabled:opacity-40 inline-flex items-center gap-1.5"
                  data-testid="bulk-approve-selected"
                >
                  <Check size={12} weight="bold" /> Approve selected ({selectedIds.length})
                </button>
                <button
                  onClick={bulkApproveSafeAll}
                  disabled={decideBusy}
                  className="text-[11px] px-2.5 py-1.5 rounded-md bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25 font-semibold inline-flex items-center gap-1.5 ml-auto"
                  data-testid="bulk-approve-safe-all"
                  title="Approve all pending games whose submitters have been on the platform 14+ days with no rejection strikes."
                >
                  <Check size={12} weight="bold" /> Approve safe (auto)
                </button>
              </div>
              {pendingGames.map(g => {
                const checked = selectedIds.includes(g.id);
                return (
                  <div key={g.id} className={`p-3 rounded-xl border flex items-center justify-between gap-3 ${checked ? 'bg-[#10B981]/[0.06] border-[#10B981]/40' : 'bg-[#0F0F16] border-[#F59E0B]/20'}`} data-testid={`pending-game-${g.id}`}>
                    <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSelect(g.id)}
                        onClick={e => e.stopPropagation()}
                        className="w-4 h-4 accent-[#10B981]"
                        data-testid={`select-pending-${g.id}`}
                      />
                    </label>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm font-semibold truncate">{g.name}</p>
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B] uppercase font-bold tracking-wider">Pending</span>
                      </div>
                      <p className="text-[#888] text-[11px] mt-0.5 truncate">
                        Submitted by {g.submitted_by_name || g.submitter?.username || 'unknown'}
                        {g.submitted_at ? ` · ${new Date(g.submitted_at).toLocaleDateString()}` : ''}
                      </p>
                    </div>
                    <div className="flex gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => approvePending(g)}
                        disabled={decideBusy}
                        className="text-[11px] px-2.5 py-1.5 rounded-md bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25 inline-flex items-center gap-1 disabled:opacity-40"
                        data-testid={`approve-game-${g.id}`}
                      ><Check size={11} weight="bold" /> Approve</button>
                      <button
                        onClick={() => { setRejectingGame(g); setRejectReason(''); }}
                        disabled={decideBusy}
                        className="text-[11px] px-2.5 py-1.5 rounded-md bg-[#EF4444]/10 text-[#EF4444] hover:bg-[#EF4444]/20 inline-flex items-center gap-1 disabled:opacity-40"
                        data-testid={`reject-game-${g.id}`}
                      ><Trash size={11} weight="bold" /> Reject</button>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* Rejection reason modal — hard-deletes the game on confirm, notifies submitter. */}
      {rejectingGame && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => !decideBusy && setRejectingGame(null)}>
          <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-[#0e0e16] border border-white/[0.08] rounded-2xl p-5" data-testid="reject-game-modal">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white text-base font-bold">Reject &ldquo;{rejectingGame.name}&rdquo;?</h2>
              <button onClick={() => setRejectingGame(null)} className="text-[#666] hover:text-white" disabled={decideBusy}><X size={18} /></button>
            </div>
            <p className="text-[12px] text-[#A1A1AA] mb-3">
              This <span className="text-[#EF4444] font-semibold">permanently deletes</span> the game and marks the submitter&rsquo;s collection row as <em>Removed</em>. They&rsquo;ll be notified.
            </p>
            <label className="block text-[10px] uppercase text-[#666] mb-1">Optional reason for the submitter</label>
            <textarea
              className="w-full bg-[#0a0a14] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm placeholder:text-[#444] focus:outline-none focus:border-[#EF4444]/50 resize-none"
              rows={3}
              placeholder="e.g. Duplicate of an existing entry; unclear name; not a real game…"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              data-testid="reject-reason-input"
            />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setRejectingGame(null)} disabled={decideBusy}
                className="flex-1 py-2 rounded-md bg-white/5 text-[#A1A1AA] text-xs font-medium hover:bg-white/10 disabled:opacity-40">
                Cancel
              </button>
              <button onClick={confirmReject} disabled={decideBusy}
                className="flex-1 py-2 rounded-md bg-[#EF4444] text-white text-xs font-semibold hover:bg-[#DC2626] disabled:opacity-40"
                data-testid="reject-game-confirm">
                {decideBusy ? 'Rejecting…' : 'Reject & Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setModalOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-[#0e0e16] border border-white/[0.08] rounded-2xl p-5 max-h-[90vh] overflow-y-auto" data-testid="game-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white text-base font-bold">{modalMode === 'add' ? 'Add Game' : 'Edit Game'}</h2>
              <button onClick={() => setModalOpen(false)} className="text-[#666] hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] uppercase text-[#666] mb-1">Name *</label>
                <input className={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="game-name-input" />
              </div>
              <div>
                <label className="block text-[10px] uppercase text-[#666] mb-1">Publisher</label>
                <input className={inp} value={form.publisher} onChange={e => setForm({ ...form, publisher: e.target.value })} />
              </div>
              <div>
                <label className="block text-[10px] uppercase text-[#666] mb-1">Description</label>
                <textarea rows={3} className={inp + ' resize-none'} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <label className="block text-[10px] uppercase text-[#666] mb-1">Categories (comma-separated)</label>
                <input className={inp} value={form.categories.join(', ')}
                  onChange={e => setForm({ ...form, categories: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase text-[#666] mb-1">Min players</label>
                  <input type="number" min={1} className={inp} value={form.min_players} onChange={e => setForm({ ...form, min_players: parseInt(e.target.value) || 1 })} />
                </div>
                <div>
                  <label className="block text-[10px] uppercase text-[#666] mb-1">Max players</label>
                  <input type="number" min={1} className={inp} value={form.max_players} onChange={e => setForm({ ...form, max_players: parseInt(e.target.value) || 1 })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase text-[#666] mb-1">Play time</label>
                  <input className={inp} placeholder="e.g. 60-90 min" value={form.play_time} onChange={e => setForm({ ...form, play_time: e.target.value })} />
                </div>
                <div>
                  <label className="block text-[10px] uppercase text-[#666] mb-1">Complexity</label>
                  <select className={inp + ' appearance-none'} value={form.complexity} onChange={e => setForm({ ...form, complexity: e.target.value })}>
                    {COMPLEXITIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase text-[#666] mb-1">Image URL</label>
                <input className={inp} placeholder="https://…" value={form.image} onChange={e => setForm({ ...form, image: e.target.value })} />
              </div>
              <div className="flex gap-2 pt-3">
                <button onClick={() => setModalOpen(false)}
                  className="flex-1 py-2 rounded-lg border border-white/[0.08] text-[#A1A1AA] text-sm hover:text-white">Cancel</button>
                <button onClick={save} disabled={saving}
                  className="flex-1 py-2 rounded-lg bg-[#9D4CDD] text-white text-sm font-semibold hover:bg-[#7C3AED] disabled:opacity-40"
                  data-testid="save-game-btn">
                  {saving ? 'Saving…' : modalMode === 'add' ? 'Create' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <DetailDrawer open={!!drawerGame || drawerLoading} onClose={() => setDrawerGame(null)} title="Game Details">
        {drawerLoading ? (
          <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-8 bg-white/5 rounded animate-pulse" />)}</div>
        ) : drawerGame && (
          <div className="space-y-4">
            <div>
              <p className="text-white font-semibold text-lg">{drawerGame.name}</p>
              <p className="text-[12px] text-[#555]">{drawerGame.publisher || 'Unknown publisher'}</p>
            </div>
            {drawerGame.description && <p className="text-[13px] text-[#888]">{drawerGame.description}</p>}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/[0.02] rounded-md p-3">
                <p className="text-[10px] text-[#444] uppercase mb-1">Players</p>
                <p className="text-white text-sm">{drawerGame.min_players}-{drawerGame.max_players}</p>
              </div>
              <div className="bg-white/[0.02] rounded-md p-3">
                <p className="text-[10px] text-[#444] uppercase mb-1">Play Time</p>
                <p className="text-white text-sm">{drawerGame.play_time || '—'}</p>
              </div>
              <div className="bg-white/[0.02] rounded-md p-3">
                <p className="text-[10px] text-[#444] uppercase mb-1">Sessions</p>
                <p className="text-[#00F0FF] text-sm font-medium">{drawerGame.session_count}</p>
              </div>
              <div className="bg-white/[0.02] rounded-md p-3">
                <p className="text-[10px] text-[#444] uppercase mb-1">Rules / Stats</p>
                <p className="text-white text-sm">{drawerGame.rules_count} / {drawerGame.stats_count}</p>
              </div>
            </div>
            {drawerGame.categories?.length > 0 && (
              <div>
                <p className="text-[10px] text-[#444] uppercase mb-2">Categories</p>
                <div className="flex flex-wrap gap-1.5">
                  {drawerGame.categories.map(c => <span key={c} className="text-[11px] px-2 py-1 rounded-md bg-white/[0.04] text-[#888]">{c}</span>)}
                </div>
              </div>
            )}
          </div>
        )}
      </DetailDrawer>
    </div>
  );
}
